import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  PlusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { TableToolbar } from "../../../../components/utils/TableToolbar";
import { useTableView } from "../../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../../components/utils/PMTableView/RightDrawer";
import { axiosWithToast, updateUrlWithException } from "../../../../components/Utils";
import { getDrivingDriversAsync } from "../../../../slices/driving/driver";
import {
  DriversDataTable,
  getDriverColumns,
} from "../../../../components/dashboard/driving_record/drivers/drivers-table";
import { DriverStatus } from "../../../../components/dashboard/driving_record/drivers/utils";
import { driverVehicleAction } from "./utils";
import axios from "../../../../api/axios";
import VehiclesListDialog from "../../vehicles/list-dialog";
import ModernEmployeeSelector from "../../../../components/utils/Selector/ModernEmployeeSelector";
import { ConfirmDialog } from "../../../../components/utils/Prompt";
import DrivingDriverDialog from "../../../../components/dashboard/driving_record/drivers/drivingDriverDialog";

export const DrivingDrivers = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const itemState = useSelector((store) => store.drivers);
  const { isLoading, totalCount } = itemState;

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  const [drawerDriver, setDrawerDriver] = useState(null);
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);

  const [controller, setController] = useState({
    filters: [],
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
  });

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      "filters"
    );
  }, [controller]);

  // Column definitions + table-view state
  const columns = useMemo(() => getDriverColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "drivers",
    columns: columnMeta,
    getSnapshot: () => ({
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    setController((c) => ({ ...c, page: 1, query: filters.query || "" }));
  };

  const getInitialFilters = () => ({ query: controller.query });

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(
      getDrivingDriversAsync({ ...controller, page: nextPage, append: true })
    );
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((ctrl) => {
    dispatch(getDrivingDriversAsync(ctrl));
  }, 250);

  useEffect(() => {
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const handleOnRefresh = () => {
    dispatch(getDrivingDriversAsync(controller));
  };

  // ── Vehicle actions (check-in / check-out / link user) ──────────────────────
  const [checkInDriver, setCheckInDriver] = useState(null);
  const [checkoutDriver, setCheckoutDriver] = useState(null);
  const [linkDriver, setLinkDriver] = useState(null);
  const [selectedLinkEmployees, setSelectedLinkEmployees] = useState([]);

  const checkInVehicleFilters = useMemo(
    () => [{ name: "active", value: true }],
    []
  );

  const handleCheckInSelect = async (vehicleId) => {
    if (!checkInDriver?.user?.id) {
      setCheckInDriver(null);
      return;
    }
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: checkInDriver.user.id,
          vehicleId,
          action: "checkin",
        }),
        null,
        null,
        () => handleOnRefresh()
      );
    } finally {
      setCheckInDriver(null);
      setDrawerDriver(null);
    }
  };

  const runDriverCheckout = async () => {
    const d = checkoutDriver;
    const userId = d?.user?.id;
    const driverId = d?.id;
    if (!userId && !driverId) {
      toast.error(t("automile.linkUser"));
      setCheckoutDriver(null);
      return;
    }
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: userId || undefined,
          driverId: userId ? undefined : driverId,
          vehicleId: d?.checked_into_vehicle?.id,
          action: "checkout",
        }),
        null,
        null,
        () => handleOnRefresh()
      );
    } finally {
      setCheckoutDriver(null);
      setDrawerDriver(null);
    }
  };

  const saveLinkedUser = async () => {
    const emp = selectedLinkEmployees[0];
    if (!linkDriver || !emp?.id) return;
    try {
      await axiosWithToast(
        axios.put(`/driving/drivers/${linkDriver.id}`, {
          driver: { user_id: emp.id },
        }),
        null,
        null,
        () => handleOnRefresh()
      );
    } finally {
      setLinkDriver(null);
      setSelectedLinkEmployees([]);
      setDrawerDriver(null);
    }
  };

  // Build drawer action buttons for the selected driver
  const drawerActions = useMemo(() => {
    if (!drawerDriver) return [];
    const actions = [];
    const needsUser = !drawerDriver?.user?.id;
    const canCheckOut = Boolean(drawerDriver?.checked_into_vehicle?.id);
    const canCheckIn = drawerDriver?.user?.id && !canCheckOut;

    if (needsUser) {
      actions.push({
        label: t("automile.linkUser"),
        icon: UserPlusIcon,
        primary: true,
        onClick: () => {
          setLinkDriver(drawerDriver);
          setSelectedLinkEmployees([]);
        },
      });
    }
    if (canCheckOut) {
      actions.push({
        label: t("automile.checkOutVehicle"),
        icon: ArrowRightOnRectangleIcon,
        onClick: () => setCheckoutDriver(drawerDriver),
      });
    }
    if (canCheckIn) {
      actions.push({
        label: t("Check in"),
        icon: ArrowLeftOnRectangleIcon,
        primary: true,
        onClick: () => setCheckInDriver(drawerDriver),
      });
    }
    return actions;
  }, [drawerDriver, t]);

  const listCount = totalCount != null ? `${totalCount}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        subtitle=""
        actions={[]}
        title={t("Drivers")}
        listCount={listCount}
        mainButton={{
          label: t("New"),
          action: () => setDriverDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="p-5 space-y-4">
          {/* Filter card */}
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={getInitialFilters()}
            searchPlaceholder={t("Search...")}
            filterTitle={t("Drivers")}
            acceptedStatuses={[]}
          />

          {/* Table card */}
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <DriversDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              density={tableView.density}
              hasMore={(totalCount || 0) > (itemState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(driver) => setDrawerDriver(driver)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerDriver}
        onClose={() => setDrawerDriver(null)}
        eyebrow={t("Drivers")}
        title={
          drawerDriver?.user?.id
            ? drawerDriver?.user?.displayTitle
            : drawerDriver?.name || t("Drivers")
        }
        actions={drawerActions}
      >
        {drawerDriver && (
          <DrawerSection>
            <DrawerProps
              items={[
                {
                  label: t("Status"),
                  value: <DriverStatus driver={drawerDriver} />,
                },
                {
                  label: t("automile.userName"),
                  value: drawerDriver.user?.displayTitle || drawerDriver.name || "—",
                },
                {
                  label: t("automile.checkedIntoVehicle"),
                  value: drawerDriver.checked_into_vehicle?.displayTitle || "—",
                },
                {
                  label: t("automile.distanceDriven"),
                  value:
                    drawerDriver.distance_driven != null
                      ? `${drawerDriver.distance_driven} km`
                      : "—",
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>

      <DrivingDriverDialog
        open={driverDialogOpen}
        onClose={() => setDriverDialogOpen(false)}
        onRefresh={handleOnRefresh}
      />

      <ConfirmDialog
        open={Boolean(checkoutDriver)}
        onClose={() => setCheckoutDriver(null)}
        onConfirm={runDriverCheckout}
        title={t("automile.checkoutConfirmTitle")}
        message={t("automile.checkoutConfirmMessage")}
        confirmText={t("automile.checkoutConfirm")}
        cancelText={t("automile.checkoutCancel")}
      />

      <VehiclesListDialog
        open={Boolean(checkInDriver)}
        onClose={() => setCheckInDriver(null)}
        checkInMode
        allowOccupiedVehicles
        filters={checkInVehicleFilters}
        onSelect={handleCheckInSelect}
      />

      <Transition.Root show={Boolean(linkDriver)} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[3600]"
          onClose={() => setLinkDriver(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 z-[3600] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="flex max-h-[min(90vh,100%)] w-full max-w-lg flex-col rounded-t-2xl bg-white pb-[max(1rem,env(safe-area-inset-bottom))] shadow-xl sm:rounded-2xl">
                <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {t("automile.linkUser")}
                  </Dialog.Title>
                  <p className="text-sm text-gray-500">{linkDriver?.name}</p>
                </div>
                <div className="min-h-0 flex-1 overflow-hidden px-2 py-2 sm:px-4">
                  <ModernEmployeeSelector
                    open={Boolean(linkDriver)}
                    selectedEmployees={selectedLinkEmployees}
                    onChange={setSelectedLinkEmployees}
                    onClose={() => setLinkDriver(null)}
                    multiple={false}
                  />
                </div>
                <div className="flex gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
                  <button
                    type="button"
                    className="flex-1 rounded-lg border border-gray-300 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setLinkDriver(null)}
                  >
                    {t("Close")}
                  </button>
                  <button
                    type="button"
                    disabled={!selectedLinkEmployees[0]}
                    className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    onClick={saveLinkedUser}
                  >
                    {t("Save")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
